import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { WS2_AMENITY_NAMES } from '../../constants/amenities';
import { wizardFontStyle } from '../../pages/OnboardingWizard/wizardTypography';
import { getDisplayApproaches } from '../../utils/parkingInstructions';
import ApproachesList from '../ApproachesList/ApproachesList';

export function getFeaturedPhotoUrl(photos) {
    if (!photos?.length) return null;
    const starred = photos.find(p => p.featured);
    const pick = starred ?? photos[0];
    return pick?.url || pick?.preview || null;
}

export default function ListingPreviewModal({ onClose, addressData, confirmedFields, photos, pricing }) {
    const heroUrl = getFeaturedPhotoUrl(photos);
    // PP-492: confirmedFields.instructions can be the new structured
    // per-approach-road object — never render it directly as a JSX child
    // (React throws on a raw object). getDisplayApproaches normalizes either
    // shape into a uniform list; a legacy string becomes a single entry.
    const instructionApproaches = getDisplayApproaches(confirmedFields.instructions);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopImmediatePropagation();
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown, true);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    return createPortal(
        <div
            className="fixed inset-0 z-[500] flex items-center justify-center bg-black/70 p-4"
            style={wizardFontStyle}
            role="dialog"
            aria-modal="true"
            aria-label="Driver preview"
            onClick={onClose}
        >
            <div
                className="relative max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto rounded-xl bg-white shadow-2xl"
                onClick={e => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="absolute top-3 right-3 z-20 rounded-full p-1 bg-white/90 text-gray-600 hover:bg-white hover:text-gray-900 shadow-sm"
                    aria-label="Close preview"
                    onClick={onClose}
                >
                    <XMarkIcon className="h-6 w-6" />
                </button>

                <div className="relative h-[220px] shrink-0 overflow-hidden">
                    {heroUrl ? (
                        <img
                            src={heroUrl}
                            alt=""
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                        />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#4263EB] to-[#1A2744]">
                            <span className="text-[11px] font-bold uppercase tracking-[0.6px] text-white/85">
                                No photo uploaded
                            </span>
                        </div>
                    )}
                    <span className="absolute top-3 left-[15px] z-10 text-xs font-medium text-white bg-black/40 px-2.5 py-1 rounded-full">
                        Preview
                    </span>
                </div>

                <div className="px-6 py-5">
                    <h2 className="font-bold text-2xl text-gray-900 leading-tight mb-1">
                        {confirmedFields.name || addressData?.name || 'Untitled property'}
                    </h2>
                    <p className="text-sm text-gray-500 mb-4">
                        {addressData?.formatted_address || 'Address not set'}
                    </p>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] font-semibold text-gray-500 mb-6">
                        {pricing.maxSpots != null ? (
                            <span>
                                <strong className="text-gray-900 font-bold">{pricing.maxSpots}</strong>
                                {' '}spots
                            </span>
                        ) : (
                            <span className="italic font-normal text-gray-400">Spots not set</span>
                        )}
                        {pricing.hourlyRate != null ? (
                            <span>
                                <strong className="text-gray-900 font-bold">
                                    ${pricing.hourlyRate.toFixed(2)}
                                </strong>
                                /hr
                            </span>
                        ) : (
                            <span className="italic font-normal text-gray-400">Rate not set</span>
                        )}
                        {confirmedFields.type ? (
                            <span>{confirmedFields.type}</span>
                        ) : (
                            <span className="italic font-normal text-gray-400">Type not set</span>
                        )}
                    </div>

                    <div className="mb-6">
                        {instructionApproaches.length > 0 ? (
                            <ApproachesList
                                approaches={instructionApproaches}
                                labelClassName="text-[11px] font-semibold text-gray-500 mb-0.5"
                                textClassName="text-[13px] text-gray-800 leading-[1.55]"
                            />
                        ) : (
                            <p className="text-[12.5px] text-gray-400 italic">
                                Not provided yet — add to help drivers find their spot.
                            </p>
                        )}
                    </div>

                    <div>
                        {confirmedFields.amenities?.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {confirmedFields.amenities.map(slug => (
                                    <span
                                        key={slug}
                                        className="text-xs bg-gray-100 border border-gray-200 rounded-full px-2.5 py-0.5 text-gray-600"
                                    >
                                        {WS2_AMENITY_NAMES[slug] || slug}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[12.5px] text-gray-400 italic">None added yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
